import {
  entriesOf,
  foldToPair,
  LOD_LABELS,
  type BidAssociation,
  type Classification,
  type Classifications,
  type Commitment,
  type DsmScopeItem,
  type Lod,
  type ScopeItemInput
} from "design-scope";
import type {
  DesignScopeClassification,
  DesignScopeListItem,
  DesignScopeLevelOfDetail,
  DesignScopePackageLink
} from "mock-data/design-scope";
import {
  lodFromApiValue,
  toApiClassificationSystem,
  toUiClassificationSystem
} from "./design-scope-list-filter";

export { lodFromApiValue };

function idString(id: string | number | null | undefined): string {
  return id == null ? "" : String(id);
}

/** Map list API classifications into the POC dual-slot shape. */
export function toPickerClassifications(
  classifications: DesignScopeListItem["classifications"] | null | undefined
): Classifications | undefined {
  if (!classifications?.length) return undefined;
  const entries: Classification[] = [];
  for (const c of classifications) {
    const system = toUiClassificationSystem(c.system);
    if (system === "CUSTOM") {
      entries.push({
        system: "CUSTOM",
        code: "",
        title: c.title,
        termId: idString(c.id) || undefined
      });
      continue;
    }
    entries.push({
      system,
      code: c.number ?? "",
      title: c.title,
      termId: idString(c.id) || undefined,
      parentId: c.parent_id == null ? null : idString(c.parent_id),
      path: c.path
    });
  }
  return foldToPair(entries);
}

function packageLinkToCommitment(c: DesignScopePackageLink): Commitment | null {
  if (!c.design_package?.id) return null;
  return {
    designPackageId: c.design_package.id,
    milestoneId: c.design_package_milestone?.id ?? null,
    lod: lodFromApiValue(c.level_of_detail?.value),
    levelOfDetailValueId: idString(c.level_of_detail?.id),
    notes: c.notes ?? ""
  };
}

/** Populate the existing modal / table / detail panel from an API list row. */
export function toModalScopeItem(item: DesignScopeListItem): DsmScopeItem {
  return {
    id: item.id,
    projectId: "",
    sequence_id: item.sequence_id,
    classification: toPickerClassifications(item.classifications),
    title: item.title ?? undefined,
    description: item.description ?? "",
    status: "NOT_STARTED",
    responsibility: item.responsibility ?? "Architect",
    notes: item.internal_notes ?? "",
    commitments: item.design_packages
      .map(packageLinkToCommitment)
      .filter((c): c is Commitment => c != null),
    bids: item.bid_packages.map(
      (b): BidAssociation => ({
        bidPackageId: b.id,
        role: "",
        primary: b.is_primary
      })
    ),
    createdAt: item.created_at,
    updatedAt: item.created_at
  };
}

function lodFromCommitment(c: Commitment): DesignScopeLevelOfDetail {
  const sequence_no = Math.min(5, Math.max(1, c.lod || 1)) as Lod;
  return {
    id: c.levelOfDetailValueId || `mock-lod-${sequence_no}`,
    value: LOD_LABELS[sequence_no],
    title: "Level of Detail"
  };
}

/**
 * Emit API `classifications[]` from the modal dual-slot shape.
 * Prefer UniFormat, then MasterFormat, then custom.
 */
function classificationsFromPair(
  c: Classifications | undefined
): DesignScopeClassification[] {
  if (!c) return [];
  const out: DesignScopeClassification[] = [];
  for (const coded of entriesOf(c)) {
    if (coded.system === "CUSTOM") {
      out.push({
        id: coded.termId ?? `custom:${coded.title}`,
        system: "custom",
        number: null,
        title: coded.title,
        parent_id: null,
        path: []
      });
      continue;
    }
    const apiSystem = toApiClassificationSystem(coded.system);
    out.push({
      id: coded.termId ?? `${apiSystem}:${coded.code}`,
      system: apiSystem,
      number: coded.code || null,
      title: coded.title,
      parent_id: coded.parentId ?? null,
      path:
        coded.path && coded.path.length
          ? coded.path
          : [{ number: coded.code, title: coded.title }]
    });
  }
  return out;
}

function splitPackageLabel(label: string, fallbackNumber: string) {
  const [num, ...rest] = label.split(" · ");
  return rest.length
    ? { number: num, title: rest.join(" · ") }
    : { number: fallbackNumber, title: label };
}

/**
 * Apply modal submit payload onto an API list row (create or update).
 * Package number/title fall back to id when entity metadata is missing.
 */
export function applyScopeItemInputToListItem(
  input: ScopeItemInput,
  existing: DesignScopeListItem | undefined,
  helpers: {
    nameOfDp: (id: string) => string;
    nameOfBp: (id: string) => string;
    nextSequenceId: number;
  }
): DesignScopeListItem {
  const now = new Date().toISOString();
  const sequence_id = existing?.sequence_id ?? helpers.nextSequenceId;
  const number =
    existing?.number ?? `DS-${String(sequence_id).padStart(3, "0")}`;

  const bid_packages = input.bids.map((b) => {
    const parts = splitPackageLabel(helpers.nameOfBp(b.bidPackageId), "BP");
    return {
      id: b.bidPackageId,
      number: parts.number,
      title: parts.title,
      is_primary: b.primary
    };
  });
  const primary =
    bid_packages.find((b) => b.is_primary) || bid_packages[0] || null;

  return {
    id: existing?.id ?? crypto.randomUUID(),
    sequence_id,
    number,
    title: input.title ?? null,
    description: input.description?.trim() ? input.description.trim() : null,
    created_at: existing?.created_at ?? now,
    responsibility: input.responsibility || null,
    internal_notes: input.notes?.trim() ? input.notes.trim() : null,
    classifications: classificationsFromPair(input.classification),
    primary_bid_package: primary
      ? { id: primary.id, number: primary.number, title: primary.title }
      : null,
    bid_packages,
    design_packages: input.commitments.map((c, idx) => {
      const parts = splitPackageLabel(
        helpers.nameOfDp(c.designPackageId),
        "DP"
      );
      const prior = existing?.design_packages[idx];
      return {
        design_package: {
          id: c.designPackageId,
          number: parts.number,
          title: parts.title
        },
        design_package_milestone: c.milestoneId
          ? {
              id: c.milestoneId,
              name: prior?.design_package_milestone?.name ?? "",
              sequence_no: prior?.design_package_milestone?.sequence_no ?? 0
            }
          : null,
        level_of_detail: lodFromCommitment(c),
        notes: c.notes?.trim() ? c.notes.trim() : null
      };
    })
  };
}
